import { router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { SegmentedControl } from "@/components/segmented-control"
import { brandAnalyticsEmvPath } from "@/routes"
import { EMV_RANGE_VALUES, type EmvRange } from "@/types/emv-analytics"

/**
 * OKS-503 — Range preset toggle for the brand EMV dashboard.
 *
 * Segmented control: `7d · 30d · 90d · All` (default 30d). Toggle
 * change → partial Inertia reload re-fetching only the EMV data props
 * (`state`, `kpis`, `progression`, `missions`, `posts_page`) plus the
 * `range` echo. The shell + tab strip + header stay mounted.
 *
 * URL query is the source of truth; the toggle is controlled via the
 * `value` prop (the page threads the URL-echoed `range` prop). Changing
 * the range resets pagination to page 1 (omit `page` → controller
 * defaults to 1).
 */
interface EmvRangeToggleProps {
  value: EmvRange
}

const RELOAD_ONLY = [
  "state",
  "range",
  "kpis",
  "progression",
  "missions",
  "posts_page",
]

export function EmvRangeToggle({ value }: EmvRangeToggleProps) {
  const { t } = useTranslation()

  const handleChange = (next: EmvRange) => {
    // Radio-group never re-fires for the already-active option, so no
    // empty-string / no-op guard is needed (unlike the old ToggleGroup).
    router.get(
      brandAnalyticsEmvPath({ range: next }),
      {},
      {
        only: RELOAD_ONLY,
        preserveScroll: true,
        preserveState: false,
      },
    )
  }

  return (
    <SegmentedControl
      value={value}
      onValueChange={handleChange}
      data-testid="emv-range-toggle"
      aria-label={t("emv.range.label")}
      className="flex-wrap"
      options={EMV_RANGE_VALUES.map((range) => ({
        value: range,
        label: t(`emv.range.${range}`),
        "data-testid": `emv-range-toggle-${range}`,
        "aria-label": t(`emv.range.${range}`),
      }))}
    />
  )
}
